import { Component, inject, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { FilterModalComponent } from '../filter-modal/filter-modal.component';

interface ColumnFilter {
	[column: string]: Set<any>
}

@Component({
	selector: 'app-table',
	templateUrl: './table.component.html',
	styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnChanges {
	@Input() benefitsReport: any[] = [];
	@Input() headersMap: { [key: string]: string } = {}
	@Input() headersDescription: { [key: string]: string } = {}
	benefitTypes: string[] = [];
	displayedColumns: string[] = [];
	dataSource = new MatTableDataSource<any>();
	columnFilters: ColumnFilter = {}
	originalData: any[] = []

	private dialog = inject(MatDialog);

	@ViewChild(MatPaginator) paginator!: MatPaginator;

	ngOnChanges(changes: SimpleChanges) {
		if (changes['benefitsReport'] && this.benefitsReport) {
			this.processBenefitsData();
			this.originalData = [...this.dataSource.data];
			this.displayedColumns = this.headers;
			this.initializeColumnFilters();

			this.dataSource.filterPredicate = this.createFilterPredicate();

			setTimeout(() => {
				this.dataSource.paginator = this.paginator;
			});
		}
	}

	get headers(): string[] {
		return this.benefitTypes.length
			? ['Venue Name', ...this.benefitTypes, 'Total Transfer Unit Value']
			: [];

	}

	initializeColumnFilters() {
		this.columnFilters = {}
		this.headers.forEach((header) => {
			this.columnFilters[header] = new Set()
		})
	}

	openFilterMenu(column: string) {
		const dialogRef = this.dialog.open(FilterModalComponent, {
			width: "500px",
			data: {
				column,
				headerLabel: this.getHeaderLabel(column),
				values: this.getUniqueValues(column),
				selectedValues: Array.from(this.columnFilters[column] || []),
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.columnFilters[column] = new Set(result);
				this.applyColumnFilters();
			}
		});
	}

	getUniqueValues(column: string): any[] {
		if (!this.originalData || !column) return []

		const values = this.originalData
			.map((row) => row[column])
			.filter((value, index, self) => self.indexOf(value) === index)
			.sort()

		return values
	}

	hasActiveFilter(column: string): boolean {
		return this.columnFilters[column]?.size > 0
	}

	createFilterPredicate() {
		return (data: any, filter: string): boolean => {
			if (!filter) return true

			let columnFilters: { [key: string]: any[] }
			try {
				columnFilters = JSON.parse(filter)
			} catch {
				return true
			}

			for (const column in columnFilters) {
				const selectedValues = columnFilters[column]
				if (selectedValues.length > 0 && !selectedValues.includes(data[column])) {
					return false
				}
			}

			return true
		}
	}

	applyColumnFilters() {
		this.dataSource.filter = this.serializeColumnFilters()
		this.dataSource.paginator?.firstPage()
	}

	applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase()

		if (filterValue) {
			this.initializeColumnFilters()
		}

		this.dataSource.filter = filterValue

		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage()
		}
	}


	private processBenefitsData(): void {
		const specialDesserts = new Set(['Sobremesa Principal ou Taxa de Rolha']);

		const getLabel = (benefit: any) =>
			benefit.type === 'dessert'
				? (specialDesserts.has(benefit.value?.trim()) ? benefit.value.trim() : 'Sobremesa')
				: (benefit.value?.trim() || 'Indefinido');

		const benefitTypesSet = new Set<string>();
		this.benefitsReport.forEach(venue => {
			venue.benefits.forEach(benefit => {
				benefitTypesSet.add(getLabel(benefit));
			});
		});

		this.benefitTypes = Array.from(benefitTypesSet).sort();
		this.displayedColumns = ['Venue Name', ...this.benefitTypes, 'Total Transfer Unit Value'];

		const tableData = this.benefitsReport.map(venue => {
			const rowData: any = {
				'Venue Name': venue.venueName,
				'Total Transfer Unit Value': 0,
			};

			this.benefitTypes.forEach(type => rowData[type] = 0);

			venue.benefits.forEach(benefit => {
				const label = getLabel(benefit);

				rowData[label] += benefit.qtd;
				rowData['Total Transfer Unit Value'] += benefit.transferUnitValue * benefit.qtd;
			});

			return rowData;
		});

		this.dataSource = new MatTableDataSource(tableData);
		this.dataSource.paginator = this.paginator;
	}

	getHeaderDescription(header: string): string {
		return this.headersDescription[header] || ""
	}

	getHeaderLabel(header: string): string {
		return this.headersMap[header] || header
	}

	private serializeColumnFilters(): string {
		const serializable: { [key: string]: any[] } = {}

		for (const column in this.columnFilters) {
			serializable[column] = Array.from(this.columnFilters[column])
		}

		return JSON.stringify(serializable)
	}
}
